<template>
  <div class="sleeve-patch-selection">
    <div
      v-if="selectedPatch == null"
      class="sleeve-patch-selection__placeholder"
      @click="handlePlaceholderClick"
    >
      <contentful-image
        :item="contentfulImage('letter-jacket-catalog-patch-selection-placeholder')"
        class="responsive-img sleeve-patch-selection__placeholder-img"
      />
      <div class="sleeve-patch-selection__placeholder-content">
        <img
          :src="svgSelectPatchIcon"
          class="sleeve-patch-selection__placeholder-icon"
        >
        <div class="sleeve-patch-selection__placeholder-text">
          Choose Your Patch
        </div>
      </div>
    </div>
    <div
      v-if="selectedPatch !== null"
      class="sleeve-patch-selection__selected-patch"
      @click="handleSelectedPatchClick"
    >
      <contentful-image
        :item="selectedPatch"
        class="responsive-img sleeve-patch-selection__selected-patch-img"
      />
    </div>
    <div
      v-if="selectedPatch"
      id="sleeve-patch-selection-inputs"
    >
      <input
        :name="selectedPatchNameInputName"
        :value="selectedPatch.Name"
        type="hidden"
      >
      <input
        :name="selectedPatchSkuInputName"
        :value="selectedPatch.SKU"
        type="hidden"
      >
      <input
        :name="selectedPatchRetailNameInputName"
        :value="selectedPatch.RetailName"
        type="hidden"
      >
      <input
        :name="selectedPatchContentfulIdInputName"
        :value="selectedPatch.Id"
        type="hidden"
      >
    </div>
    <sleeve-patch-caption
      v-if="showCaptionUi"
      :patch="selectedPatch"
    />
    <sleeve-patch-option
      v-if="showOptionsUi"
      :patch="selectedPatch"
    />
    <div
      v-if="patchHasRequiredInputs"
      class="sleeve-patch-selection__required-message"
    >
      <span class="sleeve-patch-selection__required-symbol">*</span>
      required field
    </div>
    <div
      v-if="showCaptionUi"
      class="option-product-caption__disclaimer"
    >
      Text displayed on patch may display across multiple lines.
    </div>

    <sleeve-patch-selection-modal
      v-show="showSelectionModal"
      :show-modal="showSelectionModal"
      :groups-with-patches="groupsWithPatches"
      @patch-selected="handlePatchSelected"
      @modal-close="showSelectionModal = false"
    />
  </div>
</template>

<script>
import SleevePatchCaption from './SleevePatchCaption'
import SleevePatchOption from './SleevePatchOption'
import SleevePatchSelectionModal from './SleevePatchSelectionModal/SleevePatchSelectionModal'

import svgSelectPatchIcon from './assets/select-patch-icon.svg'

import { find, filter, orderBy } from 'lodash'
import GoogleAnalytics from '@Shared/lib/google-analytics'
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'

Vue.use(VueScrollTo)

export default {
  name: 'SleevePatchSelection',
  components: {
    SleevePatchCaption,
    SleevePatchOption,
    SleevePatchSelectionModal
  },
  props: {
    model: { type: Object, default: null, required: true }
  },
  data () {
    return {
      groupsWithPatches: [],
      showSelectionModal: false,
      svgSelectPatchIcon
    }
  },
  computed: {
    content () {
      return this.$store.state.letterJackets.letterJacketContent.catalogContent
    },
    patchGroups () {
      return orderBy(this.$store.state.letterJackets.letterJacketContent.patchGroupsContent, 'ZOrder')
    },
    patches () {
      return this.$store.state.letterJackets.letterJacketContent.patchesContent
    },
    selectedPatch: {
      get () {
        return this.$store.state.letterJackets.selectedPatch
      },
      set (value) {
        this.$store.commit('setSelectedPatch', value)
      }
    },
    showCaptionUi () {
      return this.selectedPatch !== null && this.selectedPatch.CaptionLabel !== null
    },
    showOptionsUi () {
      return this.selectedPatch !== null && this.selectedPatch.Options !== null
    },
    patchHasRequiredInputs () {
      if (this.selectedPatch !== null) {
        return this.selectedPatch.IsCaptionRequired || this.selectedPatch.Options !== null
      }

      return false
    },
    selectedPatchNameInputName () {
      return `Name_${this.firstOptionProduct.CatalogPackageOptionProductID}`
    },
    selectedPatchSkuInputName () {
      return `SKU_${this.firstOptionProduct.CatalogPackageOptionProductID}`
    },
    selectedPatchRetailNameInputName () {
      return `RetailName_${this.firstOptionProduct.CatalogPackageOptionProductID}`
    },
    selectedPatchContentfulIdInputName () {
      return `ContentfulId_${this.firstOptionProduct.CatalogPackageOptionProductID}`
    },
    firstOptionProduct () {
      return this.$store.state.catalogPageCommon.firstOptionProduct
    },
    configuredPatchContentfulId () {
      return this.$store.getters.getConfiguredValue(this.selectedPatchContentfulIdInputName)
    }
  },
  beforeMount () {
    this.patchGroups.forEach(pg => {
      pg.patches = orderBy(filter(this.patches, ['Group.Id', pg.Id]), (patch) => {
        if (patch.RetailName.length > 0) {
          return patch.RetailName.trim()
        } else {
          return patch.Name.trim()
        }
      }, 'asc')

      if (pg.patches.length > 0) {
        this.groupsWithPatches.push(pg)
      }
    })
  },
  mounted () {
    if (this.configuredPatchContentfulId !== null) {
      this.selectedPatch = find(this.patches, { Id: this.configuredPatchContentfulId })
    }
  },
  methods: {
    handlePatchSelected (patch) {
      this.selectedPatch = patch
      this.patchCaptionValue = ''
      this.showSelectionModal = false
    },
    handlePlaceholderClick () {
      GoogleAnalytics.trackEvent(
        'Select Patch CTA',
        'click',
        null
      )

      this.showSelectionModal = true
    },
    handleSelectedPatchClick () {
      GoogleAnalytics.trackEvent(
        'Select Patch CTA',
        'click',
        null
      )

      this.showSelectionModal = true
    }
  }
}
</script>

<style scoped lang="scss">
.sleeve-patch-selection {
  &__placeholder {
    position: relative;

    &-img {
      cursor: pointer;
    }

    &-content {
      top: 33%;
      cursor: pointer;
      position: absolute;
      text-align: center;
      width: 100%;
    }

    &-icon {
      width: auto;
      display: block;
      margin: 0 auto 10px auto;
    }

    &-text {
      color: $letterjacket-color-blue;
      font-size: 22px;
      font-weight: 600;
    }
  }

  &__selected-patch {
    margin-bottom: 20px;

    &-img {
      cursor: pointer;
    }
  }

  &__required {
    &-symbol {
      color: $letterjacket-color-blue;
    }

    &-message {
      font-size: 16px;
      font-weight: 600;
      color: #b6b6b6;
    }
  }
}
</style>



// WEBPACK FOOTER //
// WebResources/vue/letter-jackets/catalog-details/components/SleevePatchSelection/SleevePatchSelection.vue